import React, { useCallback, useEffect, useRef, useState } from 'react';
import { readChoice, saveChoice, OPEN_EVENT } from '../../consent/consent';
import * as styles from './CookieConsent.module.css';

// Rendered on every page (wrapRootElement). Nothing renders on the server or
// on first client render, so there is no hydration mismatch and no flash for
// visitors who already chose.
const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [hasChoice, setHasChoice] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const cardRef = useRef(null);
  const customizeRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    const choice = readChoice();
    setHasChoice(choice !== null);
    if (choice === null) setOpen(true);

    const reopen = () => {
      const current = readChoice();
      returnFocusRef.current = document.activeElement;
      setHasChoice(current !== null);
      setAnalytics(current === true); // never pre-ticked without an explicit yes
      setCustomizing(true);
      setOpen(true);
      // focus after paint so screen readers announce the dialog
      requestAnimationFrame(() => cardRef.current && cardRef.current.focus());
    };
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  // Reserve room under the page so the card never hides the last thing on it.
  useEffect(() => {
    if (!open || !cardRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const body = document.body;
    const ro = new ResizeObserver(([entry]) => {
      body.style.paddingBottom = `${Math.ceil(entry.target.getBoundingClientRect().height) + 24}px`;
    });
    ro.observe(cardRef.current);
    return () => {
      ro.disconnect();
      body.style.paddingBottom = '';
    };
  }, [open]);

  const close = useCallback(() => {
    const hadFocus = cardRef.current && cardRef.current.contains(document.activeElement);
    setOpen(false);
    setCustomizing(false);
    if (!hadFocus) return;
    const target = returnFocusRef.current && document.contains(returnFocusRef.current)
      ? returnFocusRef.current
      : document.getElementById('gatsby-focus-wrapper');
    returnFocusRef.current = null;
    if (target && target.focus) target.focus();
  }, []);

  const choose = (value) => {
    saveChoice(value);
    setHasChoice(true);
    close();
  };

  // Esc is never a choice: it folds the preferences, or dismisses a reopened
  // panel for someone who already chose. (A native listener rather than an
  // onKeyDown prop, since the dialog container itself isn't interactive.)
  useEffect(() => {
    const card = cardRef.current;
    if (!open || !card) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (customizing) {
        e.stopPropagation();
        setCustomizing(false);
        if (customizeRef.current) customizeRef.current.focus();
      } else if (hasChoice) {
        e.stopPropagation();
        close();
      }
    };
    card.addEventListener('keydown', onKeyDown);
    return () => card.removeEventListener('keydown', onKeyDown);
  }, [open, customizing, hasChoice, close]);

  if (!open) return null;

  return (
    <section
      ref={cardRef}
      className={styles.card}
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      tabIndex={-1}
    >
      <div className={styles.head}>
        <span className={styles.wave} aria-hidden="true">
          <span /><span /><span /><span /><span />
        </span>
        <h2 id="cookie-consent-title" className={styles.title}>Cookies, by invitation</h2>
        {hasChoice && (
          <button type="button" className={styles.close} onClick={close} aria-label="Close cookie settings">
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
              <path d="M3.5 3.5l9 9m0-9l-9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      <div id="cookie-consent-desc">
        <p className={styles.lede}>
          Pulse only joins a meeting when it’s invited. Our analytics work the same way.
        </p>
        <p className={styles.detail}>
          With your OK, Google Analytics cookies show us which pages help. No ads, no ad tracking.
        </p>
      </div>

      <div id="cookie-consent-prefs" className={styles.prefs} hidden={!customizing}>
        <div className={styles.pref}>
          <div className={styles.prefText}>
            <span className={styles.prefName} id="cc-essential-label">Essential</span>
            <span className={styles.prefDesc} id="cc-essential-desc">
              Remembers this cookie choice. Always on; no tracking.
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked="true"
            aria-disabled="true"
            aria-labelledby="cc-essential-label"
            aria-describedby="cc-essential-desc"
            className={`${styles.toggle} ${styles.toggleOn} ${styles.toggleLocked}`}
          >
            <span className={styles.knob} />
          </button>
        </div>
        <div className={styles.pref}>
          <div className={styles.prefText}>
            <span className={styles.prefName} id="cc-analytics-label">Analytics</span>
            <span className={styles.prefDesc} id="cc-analytics-desc">
              Google Analytics, plus a first-visit cookie noting how you found us (campaign tag, referring site, landing page).
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={analytics}
            aria-labelledby="cc-analytics-label"
            aria-describedby="cc-analytics-desc"
            className={`${styles.toggle} ${analytics ? styles.toggleOn : ''}`}
            onClick={() => setAnalytics((a) => !a)}
          >
            <span className={styles.knob} />
          </button>
        </div>
        <button type="button" className={styles.save} onClick={() => choose(analytics)}>
          Save choices
        </button>
      </div>

      <div className={styles.actions}>
        <button type="button" className={styles.choice} onClick={() => choose(false)}>
          Reject
        </button>
        <button type="button" className={styles.choice} onClick={() => choose(true)}>
          Accept
        </button>
      </div>
      <div className={styles.links}>
        <button
          ref={customizeRef}
          type="button"
          className={styles.customize}
          aria-expanded={customizing}
          aria-controls="cookie-consent-prefs"
          onClick={() => setCustomizing((c) => !c)}
        >
          {customizing ? 'Hide choices' : 'Customize'}
        </button>
        <a href="/privacy#cookies" className={styles.inlineLink}>Cookie details</a>
      </div>
    </section>
  );
};

export default CookieConsent;
